<template>
  <div :class="`cs-details-view cs-details--${size}`">
    <table :class="`cs-details-layout--${layout}`">
      <colgroup>
        <col v-for="(width, index) in widths" :key="index" :width="width" />
      </colgroup>

      <tbody>
        <tr
          v-for="(rowData, rowIndex) in rows"
          :key="rowIndex"
          class="cs-details-row"
        >
          <template v-for="(item, colIndex) in rowData">
            <th
              :key="'label' + colIndex"
              class="cs-details-colon cs-details-label"
            >
              <div v-if="item.slotLabel && $scopedSlots[item.slotLabel]">
                <slot :name="item.slotLabel" :data="item" />
              </div>
              <span v-else>{{ item.label }}</span>
            </th>
            <td
              :key="'content' + colIndex"
              class="cs-details-colon cs-details-content"
              :colspan="(item.span || 1) * 2 - 1"
            >
              <div v-if="item.slotValue && $scopedSlots[item.slotValue]">
                <slot :name="item.slotValue" :data="item" />
              </div>
              <div v-else-if="item.type === 'link'">
                <a :href="item.value">{{ item.value }}</a>
              </div>
              <div v-else-if="item.type === 'day'">
                {{ item.value | dayFilter }}
              </div>
              <div v-else-if="item.type === 'daytime'">
                {{ item.value | daytimeFilter }}
              </div>
              <div v-else-if="item.type === 'json'">
                <pre>{{ item.value | jsonFilter }}</pre>
              </div>
              <div v-else-if="item.type === 'pre'">
                <pre>{{ item.value }}</pre>
              </div>
              <div v-else>
                <span>{{ item.value }}</span>
              </div>
              <i
                v-if="item.copy !== undefined && item.copy !== false"
                @click="onCopy(item, $event)"
                title="复制"
                class="cs-detail-copy el-icon-copy-document"
              ></i>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import ClipboardJS from "clipboard";
import { Message } from "ccxd-ux";
import { isEmpt, dateFormat } from "@/utils/utils";

export default {
  name: "CsDetails",
  props: {
    contents: {
      type: Object,
      required: true,
    },
  },
  filters: {
    dayFilter(val) {
      if (isEmpt(val)) {
        return "";
      }
      return dateFormat(val, "date");
    },
    daytimeFilter(val) {
      if (isEmpt(val)) {
        return "";
      }
      return dateFormat(val, "full");
    },
    jsonFilter(val) {
      return JSON.stringify(val, null, 2);
    },
  },
  computed: {
    size() {
      return this.contents.size || "medium";
    },
    spans() {
      // 列数
      return this.contents.span || 2;
    },
    widths() {
      return this.contents.widths;
    },
    layout() {
      return Object.prototype.toString.call(this.contents.widths) ===
        "[object Array]"
        ? "fixed"
        : "auto";
    },
    datas() {
      return this.contents.datas.filter((item) => {
        const visible = item.visible;
        if (typeof visible === "function") {
          return visible(item);
        } else {
          return visible !== false;
        }
      });
    },
    rows() {
      // 分行,转化为二维数组
      const datas = [...this.datas];
      const rows = [];
      let row = [];
      let count = 0;
      while (datas.length > 0) {
        const end = (rows.length + 1) * this.spans;
        const item = datas.shift();
        const span = item.span || 1;
        count += span;
        if (count <= end) {
          row.push(item);
        } else {
          count = end + span;
          rows.push(row);
          row = [item];
        }
      }
      rows.push(row);
      return rows;
    },
  },
  methods: {
    formatValue(type, value) {
      if (type === "day") {
        return this.$options.filters.dayFilter(value);
      } else if (type === "daytime") {
        return this.$options.filters.daytimeFilter(value);
      } else if (type === "json") {
        return this.$options.filters.jsonFilter(value);
      } else {
        return value;
      }
    },
    onCopy(item) {
      let value = undefined;
      // copy字段非Boolean类型的时候,复制的值设置为copy字段
      if (typeof item.copy !== "boolean") {
        value = item.copy;
      } else {
        value = this.formatValue(item.type, item.value);
      }
      const clipboard = new ClipboardJS(this.$el, {
        text: function () {
          return value;
        },
      });
      clipboard.on("success", function (e) {
        Message.success("复制成功!");
        e.clearSelection();
        clipboard.destroy();
      });

      clipboard.on("error", function () {
        Message.error("复制失败!");
        clipboard.destroy();
      });
    },
  },
};
</script>

<style scoped lang="scss">
.cs-details-view {
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  .cs-details-layout--auto {
    table-layout: auto;
  }
  .cs-details-layout--fixed {
    table-layout: fixed;
  }
  & > table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e8e8e8;
    td,
    th {
      border: 1px solid #e8e8e8;
    }
    .cs-details-row {
      &:last-child {
        border-bottom: none;
      }
    }
    .cs-details-label {
      background-color: #fafafa;
      white-space: nowrap;
      font-weight: normal;
      text-align: left;
    }
    .cs-details-content {
      word-break: break-all;
      white-space: pre-wrap;
      position: relative;
      .cs-detail-copy {
        display: none;
        cursor: copy;
        position: absolute;
        right: 0px;
        top: 0px;
        padding: 5px;
        color: #006dff;
      }
      &:hover {
        .cs-detail-copy {
          display: block;
        }
      }
      &:last-child {
        border-right: none;
      }
      pre {
        margin: 0;
      }
    }
  }
  &.cs-details--medium {
    .cs-details-label,
    .cs-details-content {
      padding: 16px 24px;
    }
  }
  &.cs-details--small {
    .cs-details-label,
    .cs-details-content {
      padding: 8px 12px;
    }
  }
  &.cs-details--mini {
    font-size: 10px;
    .cs-details-label,
    .cs-details-content {
      padding: 6px 10px;
    }
  }
}
</style>
